import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import PublicAssignedSeatingModal from "@app-contexts/public/features/seating/components/PublicAssignedSeatingModal/PublicAssignedSeatingModal.web";
import useCheckoutAddons from "@app-contexts/public/shared/checkout/hooks/useCheckoutAddons";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import { DataProvider } from "@shared/constants/common";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { EventDetailsCard } from "@shared/modules/events/components/event-details/EventDetailsCard/EventDetailsCard.web";
import EventsModal from "@shared/modules/events/components/events-modal/EventsModal";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { SeatingStateProvider } from "@shared/modules/seating/contexts/useSeatingState";
import { useTranslation } from "@shared/utils/internationalization";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import React, { useEffect, useState } from "react";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import { AnalyticsService } from "@shared/services/analytics";
import Swiper from "../Swiper.web";
import { getTicketTypes } from "./helpers";

export default function EventAddons({
  modalHeight,
}: {
  modalHeight?: string;
}): JSX.Element {
  const { t } = useTranslation("checkout");
  const { upsellEvents, allInPricing } = useCheckoutAddons();
  const { paymentVenue } = usePublicBasket();
  const { isFlagActive } = useDjangoWaffle();
  if (upsellEvents.length === 0) {
    return <></>;
  }

  const heading = (
    <Flex gap="2xs" alignItems="center">
      <Text
        fontSize="xl"
        fontWeight="bold"
        data-testid="event-addons-products-heading"
      >
        {t("You may be interested in")}
      </Text>
      <Text fontSize="md" color="gray.600" ml="2xs" mt="2xs">
        ({t("Optional")})
      </Text>
    </Flex>
  );

  return (
    <SeatingStateProvider
      isOnSeatingMapPage={false}
      disableBestAvailableSeating={
        !isFlagActive("enable_best_assigned_seating", paymentVenue)
      }
      basketType={DataProvider.Public}
    >
      <Flex
        pt="lg"
        pb="lg"
        direction="column"
        bg="bg"
        px={{ base: "lg", sm: "xl" }}
      >
        <Flex direction="column" gap="md">
          <EventAddons.EventsList
            events={upsellEvents}
            allInPricing={allInPricing}
            modalHeight={modalHeight}
            heading={heading}
          />
        </Flex>
      </Flex>
    </SeatingStateProvider>
  );
}

EventAddons.EventsList = function EventsList({
  events,
  allInPricing = false,
  modalHeight,
  analyticsSource,
  heading,
}: {
  events: PublicBaseEventSerializer[];
  allInPricing?: boolean;
  modalHeight?: string;
  analyticsSource?: string;
  heading?: React.ReactNode;
}): JSX.Element {
  const { paymentVenue } = usePublicBasket();
  const { isFlagActive } = useDjangoWaffle();
  const {
    isOpen: isEventAddonModalOpen,
    onOpen: onOpenEventAddonModal,
    onClose: onCloseEventAddonModal,
  } = useDisclosure({ defaultIsOpen: false });
  const {
    isOpen: isSeatingModalOpen,
    onOpen: onOpenSeatingModal,
    onClose: onCloseSeatingModal,
  } = useDisclosure({ defaultIsOpen: false });

  const [selectedEvent, setSelectedEvent] =
    useState<PublicBaseEventSerializer | null>(null);

  const { useRetrieve } = usePublicEventService();
  const { data: eventData, isLoading } = useRetrieve(
    Number(selectedEvent?.id),
    {
      options: {
        enabled: Boolean(selectedEvent) && Boolean(selectedEvent?.id),
        select: (data) => ({
          ...data,
          ticket_types: getTicketTypes(data?.ticket_types || []),
        }),
      },
    }
  );

  useEffect(() => {
    if (selectedEvent && eventData && selectedEvent.id === eventData.id) {
      setSelectedEvent(eventData);
    }
  }, [eventData]);

  return (
    <>
      {events.length && (
        <Track.Impression
          trackOnce
          analytics={{
            label: "addons",
            custom_params: {
              datum: "list_view",
              type: "events",
              count: events.length,
            },
          }}
        >
          <Flex w="full">
            <Swiper slidesOffsetBefore={0} heading={heading}>
              {events.map((event) => (
                <Swiper.Slide key={event.id}>
                  <Box w="xs" height="100%">
                    <EventDetailsCard
                      event={event}
                      onClick={() => {
                        AnalyticsService.buttonClick({
                          label: "addons",
                          custom_params: {
                            datum: "card_click",
                            type: "event",
                            id: event.id,
                            assigned_space: Boolean(event.assigned_space_id),
                            ...(analyticsSource && { source: analyticsSource }),
                          },
                        });

                        setSelectedEvent(event);
                        if (event.assigned_space_id) {
                          onOpenSeatingModal();
                        } else {
                          onOpenEventAddonModal();
                        }
                      }}
                      hideFavoriteIcon
                      allInPricing={allInPricing}
                    />
                  </Box>
                </Swiper.Slide>
              ))}
            </Swiper>
          </Flex>
        </Track.Impression>
      )}

      {selectedEvent && isEventAddonModalOpen && (
        <EventsModal
          isOpen
          onClose={() => {
            onCloseEventAddonModal();
            setSelectedEvent(null);
          }}
          event={selectedEvent}
          isTicketTypesLoading={isLoading}
          modalHeight={modalHeight}
          analyticsSource={analyticsSource}
        />
      )}

      {eventData &&
        selectedEvent &&
        eventData.id === selectedEvent.id &&
        isSeatingModalOpen && (
          <PublicAssignedSeatingModal
            isOpen
            onClose={onCloseSeatingModal}
            eventId={eventData.id}
            venueId={paymentVenue}
            ticketTypeFilter={getTicketTypes}
            showContinueButton={
              !isFlagActive("enable_best_assigned_seating", paymentVenue)
            }
          />
        )}
    </>
  );
};
